import React, { useEffect } from "react";
import { logout } from "../../services/authService";
import { isWhiteLabel, getAgency, getWhitelabelSettings, agencyFromHostname } from "../../services/agencyService";
import { useNavigate, useSearchParams } from "react-router-dom";
import Notification from "./Notification";
import { ReactComponent as PotionsLogo } from "../../assets/icons/potions-logo-full.svg";
import { selectAuthenticated, selectOrganization } from "../../store/slices/authUserSlice";
import { useSelector } from "react-redux";

const Header: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuthenticated = useSelector(selectAuthenticated);
  // Subscribed so the header re-renders (and re-brands) when the signed-in
  // account's details load or change
  const organization = useSelector(selectOrganization);

  const handleLogout = async () => {
    const currentAgency = getAgency();
    await logout()
    .catch((error: any) => {console.log("logout failed", error)})
    .finally(() => {
      const loginUrl = currentAgency ? `/login?agency=${currentAgency}` : "/login";
      navigate(loginUrl);
    })
  };

  // While signed in, branding follows the account (getAgency reads the
  // organization's whitelabel settings). While logged out, the ?agency=
  // query param takes priority so whitelabel login links brand correctly.
  const urlAgency = searchParams.get("agency");
  const agency = (!isAuthenticated && urlAgency) ? urlAgency : getAgency();
  const whitelabelLogoUrl = getWhitelabelSettings()?.logo_url;

  // Keep localStorage in sync with the signed-in account so login/MFA screens
  // (where the account isn't known) brand correctly — and so a whitelabel
  // agency never leaks into a non-whitelabel account on the same device.
  useEffect(() => {
    if (!isAuthenticated || !organization) return;
    const accountAgency = organization.whitelabel
      ? (agencyFromHostname(organization.whitelabel.frontend_domain || "") || 'whitelabel')
      : null;
    if (accountAgency) {
      localStorage.setItem("agency", accountAgency);
    } else {
      localStorage.removeItem("agency");
    }
  }, [isAuthenticated, organization]);

  return (
    <header>
      <div
        style={{
          padding: "0 8px",
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #E5E7EB",
          height: "48px",
        }}>
        <a href="/dashboard">
          {agency === 'spectra' ? (
            <img src="/spectra-logo-full.png" alt="Logo" style={{padding: "0 12px", width: "140px", height: "auto"}} />
          ) : (agency === 'unifiedmmg' || agency === 'instantestimate' || agency === 'authoritydigest') ? (
            <img src="/unifiedmmg-logo-full.png" alt="Logo" style={{padding: "0 12px", width: "140px", height: "auto"}} />
          ) : (agency && whitelabelLogoUrl) ? (
            <img src={whitelabelLogoUrl} alt="Logo" style={{padding: "0 12px", width: "140px", height: "auto"}} />
          ) : (
            <PotionsLogo
              style={{
                padding: "0 12px",
                width: "88px",
                height: "auto",
              }}
            />
          )}
        </a>
        <nav style={{marginLeft: "auto", padding: "0 20px"}}>
          <ul
            style={{
              listStyle: "none",
              display: "flex",
              gap: "10px",
              margin: 0,
              alignItems: "center",
            }}>
            <li>
              <Notification />
            </li>

            {!isWhiteLabel() && (
              <button type="button" className="header-button"
                onClick={() => navigate("/help")}
              >
                Help
              </button>
            )}
            {isAuthenticated ? (
              <>
                <li>
                  <button type="button" className="header-button"
                    onClick={() => navigate("/team")}
                  >
                    Team
                  </button>
                </li>
                <li>
                  <button type="button" className="header-button"
                    onClick={() => navigate("/account")}
                  >
                    Password
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className="header-button"
                    onClick={handleLogout}>
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <li>
                <button
                  type="button"
                  className="header-button"
                  onClick={() => navigate("/login")}>
                  Login
                </button>
              </li>
            )}
          </ul>
        </nav>
      </div>
    </header>
  );
};

export default Header;
